import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ArrowLeft, Users, Stethoscope, FlaskConical, Building2, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStatsPage = ({ token }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, doctors: 0, patients: 0, researchers: 0, clinic_hospital: 0 });
  const [events, setEvents] = useState([]);
  const [dailySignups, setDailySignups] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, eventsRes, dailyRes] = await Promise.all([
        fetch(`${API}/auth-events/stats`, { headers }),
        fetch(`${API}/auth-events`, { headers }),
        fetch(`${API}/auth-events/daily-signups?days=14`, { headers })
      ]);
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData);
      }
      
      if (dailyRes.ok) {
        const dailyData = await dailyRes.json();
        // Format daily signups for the chart
        const formattedDaily = (dailyData.daily_signups || []).map(item => ({
          date: new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
          count: item.count
        }));
        setDailySignups(formattedDaily);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const barData = [
    { name: 'Doctors', value: stats.doctors, fill: '#10b981' },
    { name: 'Patients', value: stats.patients, fill: '#3b82f6' },
    { name: 'Researchers', value: stats.researchers, fill: '#f59e0b' },
    { name: 'Clinics', value: stats.clinic_hospital, fill: '#f43f5e' }
  ];

  const pieData = barData.filter(d => d.value > 0);

  // Use the dedicated daily signups data from API
  const dailyData = dailySignups.length > 0 ? dailySignups : [];

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="border-zinc-800" style={{ backgroundColor: 'rgba(24, 24, 27, 0.5)', backdropFilter: 'blur(10px)' }}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-400">{title}</p>
              <p className="text-4xl font-bold text-white mt-1">{value}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {stats.total > 0 ? `${((value / stats.total) * 100).toFixed(1)}%` : '0%'} of total
              </p>
            </div>
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${color}`}>
              <Icon className="w-7 h-7 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ 
      background: '#09090b',
      backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)`,
      backgroundSize: '40px 40px'
    }}>
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Authentication Statistics</h1>
              <p className="text-xs text-zinc-500">MedPlexity Admin Analytics</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-zinc-800 bg-gradient-to-br from-violet-900/30 to-zinc-900">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-400">Total Users</p>
                    <p className="text-5xl font-bold text-white mt-1">{stats.total}</p>
                    <p className="text-xs text-violet-400 mt-2 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> All time signups
                    </p>
                  </div>
                  <div className="w-16 h-16 rounded-xl bg-violet-600 flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          <StatCard title="Doctors" value={stats.doctors} icon={Stethoscope} color="bg-emerald-600" />
          <StatCard title="Patients" value={stats.patients} icon={Users} color="bg-blue-600" />
          <StatCard title="Researchers" value={stats.researchers} icon={FlaskConical} color="bg-amber-600" />
          <StatCard title="Clinics" value={stats.clinic_hospital} icon={Building2} color="bg-rose-600" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-zinc-800" style={{ backgroundColor: 'rgba(24, 24, 27, 0.5)', backdropFilter: 'blur(10px)' }}>
            <CardHeader>
              <CardTitle className="text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Users by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="name" stroke="#71717a" fontSize={12} />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                    labelStyle={{ color: '#fafafa' }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {barData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-zinc-800" style={{ backgroundColor: 'rgba(24, 24, 27, 0.5)', backdropFilter: 'blur(10px)' }}>
            <CardHeader>
              <CardTitle className="text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#71717a' }}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                    labelStyle={{ color: '#fafafa' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Daily Signups */}
        {dailyData.length > 0 && (
          <Card className="border-zinc-800" style={{ backgroundColor: 'rgba(24, 24, 27, 0.5)', backdropFilter: 'blur(10px)' }}>
            <CardHeader>
              <CardTitle className="text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Signups</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" stroke="#71717a" fontSize={12} />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                    labelStyle={{ color: '#fafafa' }}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Auth Provider Breakdown */}
        <Card className="border-zinc-800 mt-6" style={{ backgroundColor: 'rgba(24, 24, 27, 0.5)', backdropFilter: 'blur(10px)' }}>
          <CardHeader>
            <CardTitle className="text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Authentication Methods</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {['Google', 'Email', 'OTP'].map((provider) => {
                const count = events.filter(e => e.auth_provider === provider).length;
                const percent = events.length > 0 ? ((count / events.length) * 100).toFixed(1) : 0;
                return (
                  <div key={provider} className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                    <p className="text-zinc-400 text-sm">{provider}</p>
                    <p className="text-2xl font-bold text-white">{count}</p>
                    <p className="text-xs text-zinc-500">{percent}%</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminStatsPage;
